import React, { useState } from 'react';
import PageHero from '../components/PageHero';
import AnimatedSection from '../components/AnimatedSection';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import emailjs from '@emailjs/browser';

export default function Contact() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    email: '',
    phone: '',
    vulnerability: 'Business Commercial'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMsg('');
    
    try {
      // TODO: Replace these with your actual EmailJS credentials
      const SERVICE_ID = 'YOUR_SERVICE_ID';
      const TEMPLATE_ID = 'YOUR_TEMPLATE_ID';
      const PUBLIC_KEY = 'YOUR_PUBLIC_KEY';

      // We send the form data directly to your EmailJS template
      await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          firstName: formData.firstName,
          lastName: formData.lastName,
          companyName: formData.companyName,
          email: formData.email,
          phone: formData.phone,
          vulnerability: formData.vulnerability,
        },
        PUBLIC_KEY
      );

      setIsSubmitted(true);
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        companyName: '',
        email: '',
        phone: '',
        vulnerability: 'Business Commercial'
      });

      // Hide success message after 5 seconds
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);

    } catch (error) {
      console.error("Failed to send email:", error);
      setErrorMsg("Failed to transmit request. Please ensure your EmailJS credentials are set up.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-zinc-50">
      <PageHero 
        title="Initiate Contact" 
        description="Step out of the vulnerability zone. Connect with our elite advisors to architect your custom defense strategy." 
        image="https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=2069&auto=format&fit=crop" 
      />

      <section className="py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            
            {/* Form Section */}
            <AnimatedSection className="lg:col-span-7 bg-white p-10 md:p-14 rounded-3xl shadow-2xl border border-zinc-100 relative">
              <h2 className="text-3xl font-extrabold text-zinc-900 mb-8 tracking-tight">Request a Classified Review</h2>
              
              <AnimatePresence>
                {isSubmitted && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="mb-8 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3 text-green-800"
                  >
                    <div className="bg-green-100 p-2 rounded-full text-green-600">
                      <SafeIcon icon={FiIcons.FiCheck} className="text-xl" />
                    </div>
                    <div>
                      <h4 className="font-bold">Request Securely Transmitted</h4>
                      <p className="text-sm text-green-700">Our elite advisors have received your information and will contact you shortly.</p>
                    </div>
                  </motion.div>
                )}

                {errorMsg && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="mb-8 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-800"
                  >
                    <div className="bg-red-100 p-2 rounded-full text-red-600">
                      <SafeIcon icon={FiIcons.FiAlertCircle} className="text-xl" />
                    </div>
                    <div>
                      <h4 className="font-bold">Transmission Failed</h4>
                      <p className="text-sm text-red-700">{errorMsg}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="block text-sm font-bold text-zinc-700 mb-3 uppercase tracking-wide">First Name</label>
                    <input 
                      type="text" 
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      className="w-full px-5 py-4 bg-zinc-50 rounded-xl border border-zinc-200 focus:ring-2 focus:ring-red-600 focus:border-transparent outline-none transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed" 
                      placeholder="John" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-zinc-700 mb-3 uppercase tracking-wide">Last Name</label>
                    <input 
                      type="text" 
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      className="w-full px-5 py-4 bg-zinc-50 rounded-xl border border-zinc-200 focus:ring-2 focus:ring-red-600 focus:border-transparent outline-none transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed" 
                      placeholder="Doe" 
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-zinc-700 mb-3 uppercase tracking-wide">Company Name</label>
                  <input 
                    type="text" 
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    className="w-full px-5 py-4 bg-zinc-50 rounded-xl border border-zinc-200 focus:ring-2 focus:ring-red-600 focus:border-transparent outline-none transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed" 
                    placeholder="Acme Corp" 
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="block text-sm font-bold text-zinc-700 mb-3 uppercase tracking-wide">Email Address</label>
                    <input 
                      type="email" 
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      className="w-full px-5 py-4 bg-zinc-50 rounded-xl border border-zinc-200 focus:ring-2 focus:ring-red-600 focus:border-transparent outline-none transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed" 
                      placeholder="john@example.com" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-zinc-700 mb-3 uppercase tracking-wide">Phone Number</label>
                    <input 
                      type="tel" 
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      className="w-full px-5 py-4 bg-zinc-50 rounded-xl border border-zinc-200 focus:ring-2 focus:ring-red-600 focus:border-transparent outline-none transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed" 
                      placeholder="(555) 123-4567" 
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-zinc-700 mb-3 uppercase tracking-wide">Area of Vulnerability</label>
                  <select 
                    name="vulnerability"
                    value={formData.vulnerability}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    className="w-full px-5 py-4 bg-zinc-50 rounded-xl border border-zinc-200 focus:ring-2 focus:ring-red-600 focus:border-transparent outline-none transition-all font-medium appearance-none disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="Business Commercial">Business Commercial</option>
                    <option value="General Liability">General Liability</option>
                    <option value="Workers Compensation">Workers Compensation</option>
                    <option value="Business Owner Policy (BOP)">Business Owner Policy (BOP)</option>
                    <option value="Comprehensive Audit">Comprehensive Audit</option>
                  </select>
                </div>

                <motion.button 
                  whileHover={!isSubmitting ? { scale: 1.02 } : {}} 
                  whileTap={!isSubmitting ? { scale: 0.98 } : {}} 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full bg-zinc-900 hover:bg-red-600 text-white font-bold py-5 rounded-xl transition-colors shadow-xl text-lg tracking-wide uppercase flex justify-center items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <SafeIcon icon={FiIcons.FiLoader} className="animate-spin" /> Transmitting...
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiIcons.FiSend} /> Submit Secure Request
                    </>
                  )}
                </motion.button>
              </form>
            </AnimatedSection>

            {/* Info Section */}
            <AnimatedSection delay={0.2} className="lg:col-span-5">
              <div className="bg-red-600 p-10 md:p-14 rounded-3xl shadow-2xl text-white h-full relative overflow-hidden">
                <div className="absolute top-0 right-0 -mr-16 -mt-16 text-red-500 opacity-50">
                  <SafeIcon icon={FiIcons.FiShield} className="text-[200px]" />
                </div>
                <div className="relative z-10">
                  <h3 className="text-3xl font-extrabold mb-10 tracking-tight">Direct Channels</h3>
                  
                  <div className="space-y-10">
                    <div className="flex items-start gap-5">
                      <div className="bg-white/20 p-4 rounded-2xl backdrop-blur-sm">
                        <SafeIcon icon={FiIcons.FiMapPin} className="text-2xl" />
                      </div>
                      <div>
                        <h4 className="font-bold text-lg mb-1 tracking-wide">Command Center</h4>
                        <p className="text-red-100 font-light leading-relaxed">
                          5300 West Hillsboro<br />
                          Ste 105<br />
                          Coconut Creek, FL 33073
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-5">
                      <div className="bg-white/20 p-4 rounded-2xl backdrop-blur-sm">
                        <SafeIcon icon={FiIcons.FiPhone} className="text-2xl" />
                      </div>
                      <div>
                        <h4 className="font-bold text-lg mb-1 tracking-wide">Secure Lines</h4>
                        <p className="text-red-100 font-light leading-relaxed">
                          Direct: <a href="tel:7192701028" className="hover:text-white transition-colors underline underline-offset-2">(719) 270-1028</a>
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-5">
                      <div className="bg-white/20 p-4 rounded-2xl backdrop-blur-sm">
                        <SafeIcon icon={FiIcons.FiMail} className="text-2xl" />
                      </div>
                      <div>
                        <h4 className="font-bold text-lg mb-1 tracking-wide">Encrypted Email</h4>
                        <p className="text-red-100 font-light leading-relaxed">
                          <a href="mailto:support@insurancesecretsonline.com" className="hover:text-white transition-colors">support@insurancesecretsonline.com</a>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
            
          </div>
        </div>
      </section>
    </div>
  );
}